import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
    ExecutionStatusChanged,
    ReportedIncident,
    SelectableUnloadingAddress,
    TransportAssignmentBulkUpdatePreview,
    TransportAssignmentPage,
    TransportCapacityOrderConfirmation,
    TransportOrderConfirmation,
} from '../transportAssignment/transportAssignment.types';
import { config } from '../../../config';
import {
    mapToSelectableUnloadingAddresses,
    mapToTransportAssignmentBulkUpdatePreview,
    mapToTransportAssignmentIds,
    mapTransportAssignmentPage,
} from './mappers/transportAssignment/transportAssignmentsMapper';
import { decodeResponse, validateResponse } from './responseUtil';
import {
    TransportAssignmentBulkUpdatePreviewCodec,
    TransportAssignmentPageCodec,
} from './types/transportAssignment/transportAssignmentsApi.types';
import {
    mapToApiAcknowledgedTransportCapacityOrder,
    mapToApiAcknowledgedTransportOrders,
    mapToApiArchivedTransportOrders,
    mapToApiTransportAssignmentStatusChange,
    mapToApiTransportCapacityOrderConfirmation,
    mapToApiTransportOrderConfirmation,
    mapToIncidentApi,
} from './mappers/transportAssignment/outgoingRequestMappers';
import { prepareHeaders } from './utils';
import { SelectableUnloadingAddressesCodec } from './types/transportAssignment/addressAndContactApi.types';
import { FileExport, MeansOfTransportType, VehicleId, VehicleType } from '../sharedComponents/common.types';
import { TableSort } from '../sharedComponents/table/SortedTableHeader';
import { TransportAssignmentTableColumn } from '../transportAssignment/table/TransportAssignmentTableColumns';
import { mapToApiTransportOrderExportQueryParameters } from './mappers/transportAssignment/queryParameterMapper';
import { CONTENT_TYPE_EXCEL } from './serviceOrdersApi';
import { TransportAssignmentBulkSelectionCodec } from './types/transportAssignment/transportAssignmentBulkSelectionApi.types';
import {
    mapToTransportAssignementIdsQueryBody,
    mapToTransportAssignementsQueryBody,
} from './mappers/transportAssignment/requestBodyMappers';

export const ACKNOWLEDGED_TRANSPORT_ORDERS_PATH = 'acknowledged-transport-orders';
export const ACKNOWLEDGED_TRANSPORT_CAPACITY_ORDERS_PATH = 'has-unacknowledged-external-changes';
export const CONFIRMATIONS_PATH = 'confirmations';
export const TRANSPORT_ORDERS_PATH = 'transport-orders';
export const TRANSPORT_CAPACITY_ORDERS_PATH = 'transport-capacity-orders';
export const TRANSPORT_ASSIGNMENTS_PATH = 'transport-assignments';
export const TRANSPORT_ASSIGNMENT_IDS_PATH = 'transport-assignment-ids';
export const BULK_UPDATE_PREVIEW_TRANSPORT_ASSIGNMENTS_PATH = 'bulk-update-preview-transport-assignments';
export const CHANGE_TRANSPORT_STATUS_PATH = 'statuses';
export const ARCHIVED_TRANSPORT_ORDERS_PATH = 'archived-transport-orders';
export const SELECTABLE_UNLOADING_PATH = 'selectable-unloading-addresses';
export const INCIDENT_PATH = 'incidents';
export const DOWNLOAD_EXCEL = 'exports';
export const FILE_NAME_REGEX = /filename="?([^"]+)"?$/;

export type TransportAssignmentFilterParams = {
    statuses?: FilterableApiStatus[] | undefined;
    archived?: boolean | undefined;
    requestedPickUpFrom?: Date | undefined;
    requestedPickUpTo?: Date | undefined;
    loadingLocation?: string | undefined;
    unloadingLocation?: string | undefined;
    vehicleType?: VehicleType | undefined;
    manufacturer?: string | undefined;
    meansOfTransportType?: MeansOfTransportType | undefined;
    vins?: string[] | undefined;
    meansOfTransportId?: string | undefined;
    bundleId?: string | undefined;
    externalOrderId?: string | undefined;
};

export type GetTransportAssignmentIdsQueryParams = TransportAssignmentFilterParams;

export type GetTransportAssignmentsQueryParams = TransportAssignmentFilterParams & {
    sortBy?: TableSort<TransportAssignmentTableColumn> | undefined;
    limit?: number | undefined;
};

export enum FilterableApiStatus {
    CANCELLATION = 'cancellation',
    CONFIRMED = 'confirmed',
    UNCONFIRMED = 'unconfirmed',
    UNLOADED = 'unloaded',
    LOADED = 'loaded',
}

type PostConfirmationRequestProps = {
    confirmation: TransportOrderConfirmation;
};

type PostTransportCapacityOrderConfirmationRequestProps = {
    transportCapacityOrderId: string;
    confirmation: TransportCapacityOrderConfirmation;
};

type PostAcknowledgementOfExternalTransportOrderChangesProps = {
    transportOrderIds: string[];
};

type PutAcknowledgementOfExternalTransportCapacityOrderChangesProps = {
    transportCapacityOrderId: string;
    hasUnacknowledgedExternalChanges: boolean;
};

type PostStatusTransportAssignmentProps = {
    executionStatusChanged: ExecutionStatusChanged;
};

type PostArchiveTransportAssignmentProps = {
    transportOrderIds: string[];
    isArchived: boolean;
};

type GetUnloadingAddressesProps = {
    transportOrderId: string;
};

type PostTransportAssignmentBulkUpdatePreviewProps = {
    assignmentIds: string[];
};

export type PostIncidentProps = {
    incidents: {
        incident: Pick<ReportedIncident, 'type' | 'description' | 'isEmptyRun'>;
        vehicleId: VehicleId;
        transportOrderId: string;
    }[];
};

export type GetTransportOrderExportQueryParams = {
    loadingLocation?: string;
    unloadingLocation?: string;
    statuses: FilterableApiStatus[];
    archived?: boolean | undefined;
    orderIssuedAtStart?: Date;
    orderIssuedAtEnd?: Date;
};

const transportAssignmentTag = 'TransportAssignment';

export const transportOrderApi = createApi({
    reducerPath: 'outboundApi',
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.FINISHED_VEHICLES_SERVICE,
        prepareHeaders,
    }),
    tagTypes: [transportAssignmentTag],
    // eslint-disable-next-line max-lines-per-function
    endpoints: (builder) => ({
        getTransportAssignments: builder.query<TransportAssignmentPage, GetTransportAssignmentsQueryParams>({
            query: (queryParameters) => ({
                url: TRANSPORT_ASSIGNMENTS_PATH,
                method: 'POST',
                body: mapToTransportAssignementsQueryBody(queryParameters),
                validateStatus: validateResponse(TransportAssignmentPageCodec),
            }),
            transformResponse: decodeResponse(TransportAssignmentPageCodec, mapTransportAssignmentPage),
            providesTags: [transportAssignmentTag],
        }),
        getTransportAssignmentIds: builder.mutation<string[], GetTransportAssignmentIdsQueryParams>({
            query: (queryParameters) => ({
                url: TRANSPORT_ASSIGNMENT_IDS_PATH,
                method: 'POST',
                body: mapToTransportAssignementIdsQueryBody(queryParameters),
                validateStatus: validateResponse(TransportAssignmentBulkSelectionCodec),
            }),
            transformResponse: decodeResponse(TransportAssignmentBulkSelectionCodec, mapToTransportAssignmentIds),
        }),
        postTransportAssignmentBulkUpdatePreview: builder.mutation<
            TransportAssignmentBulkUpdatePreview,
            PostTransportAssignmentBulkUpdatePreviewProps
        >({
            query: ({ assignmentIds }) => ({
                url: BULK_UPDATE_PREVIEW_TRANSPORT_ASSIGNMENTS_PATH,
                method: 'POST',
                body: { assignment_ids: assignmentIds },
                validateStatus: validateResponse(TransportAssignmentBulkUpdatePreviewCodec),
            }),
            transformResponse: decodeResponse(
                TransportAssignmentBulkUpdatePreviewCodec,
                mapToTransportAssignmentBulkUpdatePreview,
            ),
        }),
        postTransportOrderConfirmation: builder.mutation<void, PostConfirmationRequestProps>({
            query: ({ confirmation }) => ({
                url: `${TRANSPORT_ORDERS_PATH}/${CONFIRMATIONS_PATH}`,
                method: 'POST',
                body: mapToApiTransportOrderConfirmation(confirmation),
            }),
            invalidatesTags: [transportAssignmentTag],
        }),
        postTransportCapacityOrderConfirmation: builder.mutation<
            void,
            PostTransportCapacityOrderConfirmationRequestProps
        >({
            query: ({ transportCapacityOrderId, confirmation }) => ({
                url: `${TRANSPORT_CAPACITY_ORDERS_PATH}/${transportCapacityOrderId}/${CONFIRMATIONS_PATH}`,
                method: 'POST',
                body: mapToApiTransportCapacityOrderConfirmation(confirmation),
            }),
            invalidatesTags: [transportAssignmentTag],
        }),
        postAcknowledgementOfExternalTransportOrderChanges: builder.mutation<
            void,
            PostAcknowledgementOfExternalTransportOrderChangesProps
        >({
            query: ({ transportOrderIds }) => ({
                url: ACKNOWLEDGED_TRANSPORT_ORDERS_PATH,
                method: 'POST',
                body: mapToApiAcknowledgedTransportOrders(transportOrderIds),
            }),
            invalidatesTags: [transportAssignmentTag],
        }),
        putAcknowledgementOfExternalTransportCapacityOrderChanges: builder.mutation<
            void,
            PutAcknowledgementOfExternalTransportCapacityOrderChangesProps
        >({
            query: ({ transportCapacityOrderId, hasUnacknowledgedExternalChanges }) => ({
                url: `${TRANSPORT_CAPACITY_ORDERS_PATH}/${transportCapacityOrderId}/${ACKNOWLEDGED_TRANSPORT_CAPACITY_ORDERS_PATH}`,
                method: 'PUT',
                body: mapToApiAcknowledgedTransportCapacityOrder(hasUnacknowledgedExternalChanges),
            }),
            invalidatesTags: [transportAssignmentTag],
        }),
        postStatusTransportAssignment: builder.mutation<void, PostStatusTransportAssignmentProps>({
            query: ({ executionStatusChanged }) => ({
                url: `${TRANSPORT_ORDERS_PATH}/${CHANGE_TRANSPORT_STATUS_PATH}`,
                method: 'POST',
                body: mapToApiTransportAssignmentStatusChange(executionStatusChanged),
            }),
            invalidatesTags: [transportAssignmentTag],
        }),
        postArchiveTransportAssignment: builder.mutation<void, PostArchiveTransportAssignmentProps>({
            query: (archivedTransportOrders) => ({
                url: ARCHIVED_TRANSPORT_ORDERS_PATH,
                method: 'POST',
                body: mapToApiArchivedTransportOrders(archivedTransportOrders),
            }),
            invalidatesTags: [transportAssignmentTag],
        }),
        getUnloadingAddresses: builder.query<SelectableUnloadingAddress[], GetUnloadingAddressesProps>({
            query: (props) => ({
                url: `${TRANSPORT_ORDERS_PATH}/${props.transportOrderId}/${SELECTABLE_UNLOADING_PATH}`,
                validateStatus: validateResponse(SelectableUnloadingAddressesCodec),
            }),
            transformResponse: decodeResponse(SelectableUnloadingAddressesCodec, mapToSelectableUnloadingAddresses),
        }),
        postIncident: builder.mutation<void, PostIncidentProps>({
            query: (postIncidentProps) => ({
                url: `${TRANSPORT_ORDERS_PATH}/${INCIDENT_PATH}`,
                method: 'POST',
                body: mapToIncidentApi(postIncidentProps),
            }),
            invalidatesTags: [transportAssignmentTag],
        }),
        getTransportOrderExport: builder.mutation<FileExport, GetTransportOrderExportQueryParams>({
            query: (queryParameters) => ({
                url: `${TRANSPORT_ORDERS_PATH}/${DOWNLOAD_EXCEL}`,
                method: 'GET',
                params: mapToApiTransportOrderExportQueryParameters(queryParameters),
                cache: 'no-cache',
                responseHandler: async (response): Promise<FileExport> => {
                    if (response.headers.get('content-type') === CONTENT_TYPE_EXCEL) {
                        return {
                            fileLink: window.URL.createObjectURL(await response.blob()),
                            fileName: response.headers.get('Content-Disposition')?.match(FILE_NAME_REGEX)?.at(1),
                        };
                    } else {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                        return response.json();
                    }
                },
            }),
        }),
    }),
});

export const {
    useGetTransportAssignmentsQuery,
    useGetTransportAssignmentIdsMutation,
    usePostTransportAssignmentBulkUpdatePreviewMutation,
    usePostTransportOrderConfirmationMutation,
    usePostTransportCapacityOrderConfirmationMutation,
    usePostAcknowledgementOfExternalTransportOrderChangesMutation,
    usePutAcknowledgementOfExternalTransportCapacityOrderChangesMutation,
    usePostStatusTransportAssignmentMutation,
    usePostArchiveTransportAssignmentMutation,
    useGetUnloadingAddressesQuery,
    usePostIncidentMutation,
    useGetTransportOrderExportMutation,
} = transportOrderApi;

export const matchAssignmentsReceived = transportOrderApi.endpoints.getTransportAssignments.matchFulfilled;
